import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { fileCoverPath, fileCoverRequestsPath, fill, t, type CoverPageAnswer, type CoverRequestAnswer } from '@bms2/shared';
import { AlertBanner } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { PageModule } from '../../shell/PageModule';
import { FILES_PATH, post, useApi } from './api';
import styles from './CoverRequiredPage.module.css';

/**
 * 🔒 The Cover Required page (piece 9; decision 9680, T3368). A file the cover refuses, opened in a browser, lands
 * here: piece 9's sentence, the account that asks for cover, the signed-in person and the file. Request Cover asks
 * HR, by a Manual to-do to each holder of the HR pages permission, to upload the person's employment agreement; no
 * mail is sent. A person covered since sees the way back to the file instead.
 */
export function CoverRequiredPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { fileId = '' } = useParams();
  const page = useApi<CoverPageAnswer>(fileId ? fileCoverPath(fileId) : null, apiBaseUrl);
  const [requested, setRequested] = useState<CoverRequestAnswer | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post<CoverRequestAnswer>(fileCoverRequestsPath(fileId), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    setRequested(answer.data);
  };
  const openFile = () => window.location.assign(`${apiBaseUrl}${FILES_PATH}/${encodeURIComponent(fileId)}/stream`);
  return (
    <PageModule title={t('cover.title')}>
      <AsyncStatePanel state={page.state} emptyAction={t('cover.open')} onRetry={page.reload}>
        {(view) => (
          <div className={styles.page} data-cover-page={fileId} data-cover-refused={view.refused ? 'true' : 'false'}>
            {!view.refused ? (
              <AlertBanner tone="good" message={t('cover.covered')} action={{ label: t('cover.open'), onAction: openFile }} />
            ) : requested && requested.asked > 0 ? (
              <AlertBanner tone="good" message={fill(t('cover.requested'), { person: requested.person })} />
            ) : requested ? (
              <AlertBanner tone="warning" urging message={t('cover.requestedNone')} />
            ) : (
              <AlertBanner tone="warning" message={view.sentence ?? ''} action={{ label: t('cover.request'), onAction: () => { if (!busy) void request(); } }} />
            )}
            {refusal ? <AlertBanner tone="warning" urging message={refusal} /> : null}
            <DetailList look="separator">
              <DetailRow title={view.account ?? '—'} detail={t('cover.account')} data-cover-account={view.account ?? ''} />
              <DetailRow title={view.person} detail={t('cover.person')} data-cover-person={view.person} />
              <DetailRow title={view.file} detail={t('cover.file')} />
            </DetailList>
          </div>
        )}
      </AsyncStatePanel>
    </PageModule>
  );
}
